import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, sahaAPI } from './services/apiClient';
import './design-system/styles/pages/dashboard-content.css';
import './styles/tw.css';

interface DashboardData {
    openServices: number;
    readyForDelivery: number;
    sahaAcik: number;
    sahaBugun: { ziyaret: number; ciro: number; kar: number; ucretsiz: number } | null;
    totalServices: number;
    inProgressCount: number;
    recentServices: any[];
    loading: boolean;
    waitingForParts: number;
    openedToday: number;
    pendingPayments: number;
    completionRate: number;
    openWorkOrders: number;
    completedWorkOrders: number;
    newWorkOrdersToday: number;
    bugunIslem: number;
    bugunIslemDetay?: { satis: number; alis: number; dekont: number; servis: number; masraf: number };
    totalPersonnel: number;
    subscriptionStatus: string;
    trialDaysRemaining: number;
    trialEndDate: Date | null;
    isTrialActive: boolean;
    totalStocks: number;
    lowStocks: number;
    outOfStocks: number;
    financial?: {
        todaySales: number;
        monthlySales: number;
        todaySalesUSD?: number;
        monthlySalesUSD?: number;
        todayNonCashPayments?: number;
        monthlyNonCashPayments?: number;
        todayPaymentMethods?: Array<{ method: string; total: number }>;
        monthlyPaymentMethods?: Array<{ method: string; total: number }>;
        todayExpenses: number;
        monthlyExpenses: number;
        totalReceivables: number;
        totalReceivablesUSD?: number;
        totalCashBalance: number;
        todayCashIncome: number;
        todayCashExpense: number;
        netCashFlow: number;
        monthlyProfit: number;
        todayProfit?: number;
    };
    isAdmin: boolean;
}

const DashboardContent: React.FC = () => {
    const { user, selectedRole } = useAuth();
    const navigate = useNavigate();

    const [isFinancialHidden, setIsFinancialHidden] = useState<boolean>(() => {
        // localStorage'dan gizleme durumunu oku
        const saved = localStorage.getItem('financialHidden');
        return saved === 'true';
    });

    // 03.10.2026: uyarı şeridi ve durum kartları kalktı → /api/alerts isteği de kaldırıldı (Uyarılar sayfası kendi çeker).

    /**
     * FAZLA ÖDEME AÇILIMI — kutudaki toplamın kimlerden oluştuğu.
     * Kutuya tıklanınca kutunun altında açılır (ayrı sayfa değil: birkaç isim,
     * panelden kopmaya değmez). Her açılışta TAZE çekilir — biri az önce ödeme
     * aldıysa eski liste gösterilmesin.
     */
    const [avansAcik, setAvansAcik] = useState(false);
    const [avansListe, setAvansListe] = useState<Array<{
        id: string; ad: string; phone: string | null; tutar: number;
    }> | null>(null);
    const [avansHata, setAvansHata] = useState<string | null>(null);

    const avansAcKapa = () => {
        if (avansAcik) { setAvansAcik(false); return; }
        setAvansAcik(true);
        setAvansListe(null);
        setAvansHata(null);
        dashboardAPI.getFazlaOdemeler()
            .then((r: any) => {
                if (r?.data?.success) setAvansListe(r.data.data || []);
                else setAvansHata('Liste alınamadı.');
            })
            .catch(() => setAvansHata('Liste alınamadı.'));
    };

    /**
     * BUGÜNKÜ SAHA İŞLERİ — sağ sütun (02.10.2026, Stitch tasarımı).
     * Saha ekranıyla AYNI uç (`sahaAPI.liste('acik')`). Hata yutulur: kart
     * "saha işi yok" der, panel etkilenmez.
     */
    const [sahaIsleri, setSahaIsleri] = useState<Array<{
        id: string; servisNo: string | null; cihaz: string | null; ariza: string | null;
        randevuTarihi: string | null; randevuSaati: string | null; adres: string | null;
        teknisyen: string | null; durum: string; musteri: { ad: string | null; bolge?: string | null };
    }> | null>(null);

    useEffect(() => {
        let iptal = false;
        sahaAPI.liste('acik')
            .then((r: any) => { if (!iptal) setSahaIsleri(r?.data?.data || []); })
            .catch(() => { if (!iptal) setSahaIsleri([]); });
        return () => { iptal = true; };
    }, []);

    const [dashboardData, setDashboardData] = useState<DashboardData>({
        openServices: 0,
        readyForDelivery: 0,
        sahaAcik: 0,
        sahaBugun: null,
        totalServices: 0,
        inProgressCount: 0,
        recentServices: [],
        loading: true,
        waitingForParts: 0,
        openedToday: 0,
        pendingPayments: 0,
        completionRate: 0,
        openWorkOrders: 0,
        completedWorkOrders: 0,
        newWorkOrdersToday: 0,
        bugunIslem: 0,
        totalPersonnel: 0,
        subscriptionStatus: 'unknown',
        trialDaysRemaining: 0,
        trialEndDate: null,
        isTrialActive: false,
        totalStocks: 0,
        lowStocks: 0,
        outOfStocks: 0,
        isAdmin: false
    });


    // Backend API'den dashboard verilerini çek
    const fetchDashboardData = useCallback(async () => {
        if (!user) return;

        setDashboardData(prev => ({ ...prev, loading: true }));

        try {

            // Paralel olarak tüm verileri çek
            // Saha performansı AYRI uçtan geliyor ve hata verirse panel çökmemeli:
            // .catch(null) ile sessizce boş geçiyor, kutu 0 gösteriyor.
            const [metricsResponse, recentServicesResponse, sahaResponse] = await Promise.all([
                dashboardAPI.getMetrics(),
                dashboardAPI.getRecentServices({ limit: 10 }),
                sahaAPI.performans().catch(() => null),
            ]);

            const sahaOzet = (sahaResponse as any)?.data?.data?.toplam || null;

            const metrics = metricsResponse.data?.data || {};
            const recentServices = recentServicesResponse.data?.data || [];

            // Admin kontrolü - financial veriler varsa admin'dir
            // ✅ FIX: Backend'den gelen isAdmin değerini kullan (güvenli)
            const isAdmin = metrics.isAdmin === true;


            // State'i güncelle
            setDashboardData({
                openServices: metrics.openServices || 0,
                readyForDelivery: metrics.readyForDelivery || 0,
                sahaAcik: metrics.sahaAcik || 0,
                sahaBugun: sahaOzet
                    ? { ziyaret: sahaOzet.ziyaret || 0, ciro: sahaOzet.ciro || 0,
                        kar: sahaOzet.kar || 0, ucretsiz: sahaOzet.ucretsiz || 0 }
                    : null,
                totalServices: metrics.totalServices || 0,
                inProgressCount: metrics.inProgressCount || 0,
                waitingForParts: metrics.waitingForParts || 0,
                openedToday: metrics.openedToday || 0,
                bugunIslem: metrics.bugunIslem || 0,
                bugunIslemDetay: metrics.bugunIslemDetay,
                pendingPayments: metrics.pendingPayments || 0,
                completionRate: metrics.completionRate || 0,
                openWorkOrders: metrics.openWorkOrders || 0,
                completedWorkOrders: metrics.completedWorkOrders || 0,
                newWorkOrdersToday: metrics.newWorkOrdersToday || 0,
                totalPersonnel: metrics.totalPersonnel || 0,
                totalStocks: metrics.totalStocks || 0,
                lowStocks: metrics.lowStocks || 0,
                outOfStocks: metrics.outOfStocks || 0,
                recentServices: recentServices,
                loading: false,
                subscriptionStatus: dashboardData.subscriptionStatus || 'active',
                trialDaysRemaining: dashboardData.trialDaysRemaining || 0,
                trialEndDate: dashboardData.trialEndDate || null,
                isTrialActive: dashboardData.isTrialActive || false,
                financial: metrics.financial,
                isAdmin
            });

        } catch (error) {
            setDashboardData(prev => ({ ...prev, loading: false }));
        }
    }, [user]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Event listeners for real-time updates
    useEffect(() => {
        const handleCustomerAdded = () => {
            fetchDashboardData();
        };
        
        const handleServiceUpdated = () => {
            fetchDashboardData();
        };
        
        window.addEventListener('customerAdded', handleCustomerAdded as EventListener);
        window.addEventListener('serviceUpdated', handleServiceUpdated as EventListener);
        
        return () => {
            window.removeEventListener('customerAdded', handleCustomerAdded as EventListener);
            window.removeEventListener('serviceUpdated', handleServiceUpdated as EventListener);
        };
    }, [fetchDashboardData]);

    // Yardımcı fonksiyonlar
    const formatNumber = (number: number | undefined): string => {
        return new Intl.NumberFormat('tr-TR').format(number || 0);
    };

    const formatNumberHidden = (number: number | undefined): string => {
        if (isFinancialHidden) {
            return '***';
        }
        return formatNumber(number);
    };

    const handleToggleFinancialVisibility = () => {
        const newState = !isFinancialHidden;
        setIsFinancialHidden(newState);
        localStorage.setItem('financialHidden', String(newState));
    };

    const formatDate = (date: unknown): string => {
        if (!date) return '-';
        try {
            const getDateValue = (dateField: unknown): Date => {
                if (typeof dateField === 'object' && dateField !== null && 'toDate' in dateField && typeof (dateField as { toDate?: () => Date }).toDate === 'function') {
                    return (dateField as { toDate: () => Date }).toDate();
                }
                if (dateField instanceof Date) return dateField;
                return new Date(dateField as string | number);
            };
            const dateObj = getDateValue(date);
            return dateObj.toLocaleDateString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return '-';
        }
    };

    const getStatusText = (status: string | undefined): string => {
        if (!status) return 'Bilinmiyor';
        
        const statusLower = status.toLowerCase();
        switch (statusLower) {
            case 'kabul-edildi':
            case 'kabul edildi':
                return 'Kabul Edildi';
            case 'isleniyor':
            case 'işlemde':
            case 'devam ediyor':
            case 'devam-ediyor':
                return 'İşlemde';
            case 'dis-serviste':
            case 'dış serviste':
                return 'Dış Serviste';
            case 'teslim-edildi':
            case 'teslim edildi':
                return 'Teslim Edildi';
            case 'teslim-hazir':
            case 'teslim hazır':
            case 'teslim edilecek':
                return 'Teslim Hazır';
            case 'parca-bekliyor':
            case 'parca-bekleniyor':
            case 'parça bekliyor':
            case 'parça bekleniyor':
                return 'Parça Bekleniyor';
            case 'geri gönderildi':
            case 'geri-gonderildi':
                return 'Geri Gönderildi';
            case 'beklemede':
                return 'Beklemede';
            case 'tamamlandi':
            case 'tamamlandı':
                return 'Tamamlandı';
            case 'randevu-olusturuldu':
            case 'randevu oluşturuldu':
            case 'randevu olusturuldu':
            case 'randevu-olusturuldu':
                return 'Randevu';
            default: 
                // Eğer status içinde "randevu" geçiyorsa kısalt
                if (statusLower.includes('randevu')) {
                    return 'Randevu';
                }
                return status || 'Bilinmiyor';
        }
    };


    // Loading state
    if (dashboardData.loading) {
        return (
            <div className="dashboard-content-container">
                <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Dashboard yükleniyor...</p>
                </div>
            </div>
        );
    }

    /* ═══════════════════════════════════════════════════════════════════
       DURUM ŞERİDİ
       Referans olarak verilen panelde günün özeti soluk gri kutucuklarda
       değil, renkli ve iri kartlarda duruyor: her kartta koyu ikon bloğu,
       büyük sayı ve altta ince bir açıklama şeridi. Rakamlar uzaktan
       okunuyor, renk de durumu tek bakışta anlatıyor.

       Sınıflar dizide TAM METİN olarak yazılı — Tailwind kaynak dosyayı
       metin olarak tarar, `bg-${renk}-500` gibi birleştirilmiş adları
       göremez ve o sınıfı hiç üretmez.
       ═══════════════════════════════════════════════════════════════════ */
    // 03.10.2026: durum kartları kaldırıldı; kart verileri (vadesiGecmis, bugunIslemAlt) de kalktı.


    /* ═══════════════════════════════════════════════════════════════════
       Aşağıdaki dört panelin verisi ZATEN /dashboard/metrics yanıtında
       geliyordu ama ekranda hiç kullanılmıyordu: stok sayıları, günün kasa
       hareketi, ödeme tipi kırılımı ve servis akışı. Yeni uç nokta
       yazılmadı; sadece gelen veri görünür hâle getirildi.
       ═══════════════════════════════════════════════════════════════════ */

    const fin = dashboardData.financial;
    const finansGorunur = dashboardData.isAdmin && fin && (selectedRole === 'admin' || selectedRole === 'owner');

    /* Bugünkü tahsilat kırılımı. Sunucu boş dizi/eksik gönderirse blok hiç
       çizilmez — "0 tahsilat" satırları paneli kirletmesin. */
    const tahsilatlar: Array<{ yontem: string; tutar: number; adet: number }> =
        Array.isArray((fin as any)?.bugunTahsilat) ? (fin as any).bugunTahsilat : [];
    const tahsilatToplam: number = Number((fin as any)?.bugunTahsilatToplam || 0);
    /* Bugün kasaya GİRMEYEN satış: veresiye kalan tutar. "Satış 5.425 ₺ ama
       tahsilat 3.588 ₺" farkının cevabı bu — para kaybolmadı, cariye yazıldı. */
    /* Satış kârı — gider düşülmemiş. Ciro satırının kâr karşılığı. */
    const aylikTahsilat: number = Number((fin as any)?.aylikTahsilat || 0);
    const gecikmisAlacak: number = Number((fin as any)?.gecikmisAlacak || 0);
    const gecikmisAdet: number = Number((fin as any)?.gecikmisAdet || 0);
    const musteriAvansi: number = Number((fin as any)?.musteriAvansi || 0);
    const avansAdet: number = Number((fin as any)?.avansAdet || 0);
    const stokDegeri: number = Number((fin as any)?.stokDegeri || 0);
    const giderKirilim: Array<{ kategori: string; tutar: number }> =
        Array.isArray((fin as any)?.gunlukGiderKirilim) ? (fin as any).gunlukGiderKirilim : [];
    const satisKariGun: number = Number((fin as any)?.todaySalesGrossProfit || 0);
    const satisKariAy: number = Number((fin as any)?.monthlySalesGrossProfit || 0);

    const veresiye: { adet: number; tutar: number } =
        (fin as any)?.bugunVeresiye || { adet: 0, tutar: 0 };


    /**
     * Finansal özetin kartı: ETİKET · RAKAM · ALT SATIR.
     *
     * Alt satır boş bırakılmaz — her rakamın yanında onu açan bir cümle durur
     * ("Kâr 5.422 ₺", "Veresiye 1.837 ₺", "Aldık, vermedik"). Eski tasarımda
     * yalnız rakam vardı ve "Toplam Alacak 367.768 ₺" gibi sayılar bağlamsız
     * kalıyordu; iyi mi kötü mü, ne kadarı riskli, görünmüyordu.
     *
     * `altRenk` alt satırın, `renk` ana rakamın rengi. Ayrı tutuldular çünkü
     * gider kartında rakam kırmızı ama alt satır (kırılım) nötr olmalı.
     */
    /**
     * Finansal özetin TEK hücre dili: ETİKET · RAKAM · ALT SATIR.
     *
     * Bütün bölümler bunu kullanır; ayrı kutu tasarımları yok. Hücre kendi
     * çerçevesini çizmez — çerçeve ve ayraçlar kapsayıcı ızgaradan gelir
     * (`divide-x divide-y`). Böylece 12 ayrı kutu yerine tek bir tablo okunur.
     *
     * Alt satır asla boş bırakılmaz: her rakamın yanında onu açan bir cümle
     * durur. "Alacak 367.768 ₺" tek başına iyi mi kötü mü belli değildi;
     * "233.136 ₺ gecikmiş" ile anlam kazanıyor.
     *
     * `tikla` verilirse hücre düğmeye dönüşür (ok işareti otomatik).
     */
    const hucre = (
        etiket: string,
        deger: number | undefined,
        alt: string,
        altRenk = 'text-slate-400',
        renk = 'text-slate-900',
        tikla?: () => void,
        ozelDeger?: string,      // ₺ olmayan gösterim (ör. "%44")
    ) => {
        const icerik = (
            <>
                <div className="flex items-baseline gap-1">
                    <span className="truncate text-[9.5px] font-semibold uppercase tracking-wide text-slate-500 sm:text-[10px]">
                        {etiket}
                    </span>
                    {tikla && <i className="fas fa-chevron-right text-[8px] text-slate-400" />}
                </div>
                <div className={`truncate text-[13.5px] font-bold tabular-nums leading-tight sm:text-[14.5px] ${renk}`}>
                    {ozelDeger !== undefined
                        ? ozelDeger
                        : <>{formatNumberHidden(deger)} <span className="text-[10.5px] font-semibold sm:text-[11px]">₺</span></>}
                </div>
                <div className={`truncate text-[10px] leading-tight sm:text-[10.5px] ${altRenk}`} title={alt}>{alt}</div>
            </>
        );
        return tikla ? (
            <button key={etiket} type="button" onClick={tikla}
                    className="bg-white px-2 py-1.5 text-left transition-colors hover:bg-slate-50 sm:px-2.5">
                {icerik}
            </button>
        ) : (
            <div key={etiket} className="bg-white px-2 py-1.5 sm:px-2.5">{icerik}</div>
        );
    };

    /** Bölüm ayracı — ince çizgi üstünde küçük başlık. */
    const bolumBasligi = (metin: string) => (
        <div className="mb-1 mt-2.5 flex items-baseline gap-2 first:mt-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{metin}</span>
            <span className="h-px flex-1 bg-slate-200" />
        </div>
    );

    /* Kartın simge rengi. Eski renkli dolgu kartlar yerine (02.10.2026, Stitch
       "Operasyon & Yönetim Paneli") beyaz kart + renkli simge kutusu. Renk
       yarı saydam verildiği için açık ve koyu temada aynı kural çalışır. */



    /* Saha kartı: önce BUGÜNÜN randevuları (saate göre), yoksa en yakın açık işler. */
    const bugunIso = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD, yerel saat
    const sahaGosterilen = (sahaIsleri || [])
        .slice()
        .sort((a, b) => {
            const ab = (a.randevuTarihi || '').slice(0, 10) === bugunIso ? 0 : 1;
            const bb = (b.randevuTarihi || '').slice(0, 10) === bugunIso ? 0 : 1;
            if (ab !== bb) return ab - bb;
            return `${a.randevuTarihi || '9'}${a.randevuSaati || ''}`.localeCompare(`${b.randevuTarihi || '9'}${b.randevuSaati || ''}`);
        })
        .slice(0, 5);
    const sahaBugunSayisi = (sahaIsleri || []).filter((s) => (s.randevuTarihi || '').slice(0, 10) === bugunIso).length;

    return (
        <div className="tw-alan dashboard-content-container panel-v2 font-sans text-slate-700 antialiased [&_*]:box-border">

            {/* ══════════════ HIZLI EYLEMLER ══════════════
                03.10.2026 (Arif): sayfanın EN ÜSTÜNE alındı. */}
            {/* 02.10.2026: renkli zeminli düğmeler sade beyaza döndü (Stitch tasarımı);
                renk yalnız simge kutusunda — sayfada tek vurgu rengi kalsın. */}
            <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] sm:grid sm:grid-cols-2 sm:gap-2.5 sm:overflow-visible sm:pb-0 md:grid-cols-4 [&::-webkit-scrollbar]:hidden">
                {[
                    { etiket: 'Müşteriler',    ikon: 'fa-users',         yol: '/customer/customer-list',            ikonSinif: 'bg-blue-600' },
                    { etiket: 'Yeni Müşteri',  ikon: 'fa-user-plus',     yol: '/customer/new-customer',             ikonSinif: 'bg-emerald-600' },
                    { etiket: 'Stok Listesi',  ikon: 'fa-boxes-stacked', yol: '/stock/stock-list',                  ikonSinif: 'bg-violet-600' },
                    { etiket: 'Masraf Girişi', ikon: 'fa-receipt',       yol: '/finance/expense-screen',    ikonSinif: 'bg-rose-600' },
                    { etiket: 'Kasa Durumu',   ikon: 'fa-cash-register', yol: '/finance/cash-status',       ikonSinif: 'bg-amber-600' },
                    { etiket: 'İşlem Listesi', ikon: 'fa-list-ul',       yol: '/invoices/all-transactions',                 ikonSinif: 'bg-cyan-600' },
                    // 📍 Saha ekibi telefondan buraya basıp müşterinin kapısında
                    //    konumu işaretliyor (2026-08-30).
                    { etiket: 'Konum Belirle', ikon: 'fa-location-dot',  yol: '/customer/konum-belirle',            ikonSinif: 'bg-teal-600' },
                    // 🚚 Saha teknisyeni günün işlerini buradan yürütür (2026-08-30).
                    { etiket: 'Saha Servisi',  ikon: 'fa-truck-fast',    yol: '/service/saha-servis',               ikonSinif: 'bg-purple-600' },
                ].map((e) => (
                    <button
                        key={e.etiket}
                        type="button"
                        onClick={() => navigate(e.yol)}
                        className="flex min-w-0 shrink-0 items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left font-semibold text-slate-800 transition-colors hover:border-slate-300 hover:bg-slate-50 sm:shrink"
                    >
                        <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[13px] text-white ${e.ikonSinif}`}>
                            <i className={`fas ${e.ikon}`} />
                        </span>
                        <span className="truncate text-[13.5px]">{e.etiket}</span>
                    </button>
                ))}
            </div>

            {/* 03.10.2026 (Arif): "Dikkat & Riskler" şeridi KALDIRILDI — uyarılar /uyarilar sayfasında. */}
            {/* ══════════════ FİNANSAL ÖZET ══════════════
                03.10.2026 (Arif): "Genel Bakış" başlığı kalktı, finansal özet EN ÜSTE alındı
                — rakamlar sayfa açılınca ilk görülen şey olsun.
                31.08.2026 — üçüncü tur: TEK BİR HÜCRE DİLİ.

                🔴 Önceki hâlde 12 ayrı yuvarlak kutu + ayrı tasarımlı tahsilat
                   satırları + ayrı bir saha düğmesi vardı. Her biri kendi
                   çerçevesini, gölgesini, dolgusunu taşıyınca sayfa parçalı
                   görünüyordu ve dikeyde çok yer kaplıyordu.

                ŞİMDİ: bölüm başına TEK çerçeve, içinde ince çizgilerle ayrılmış
                hücreler. Bütün hücreler aynı ölçüde (etiket 10px · rakam 14.5px ·
                alt satır 10.5px). Tahsilat ve saha satırları da AYNI dili
                konuşuyor — böylece göz tek bir ızgara okuyor, sekiz farklı kutu
                değil.

                Bölümler değişmedi, her biri tek soruya cevap:
                   BUGÜN · BU AY · DURUM · (tahsilat kırılımı) · (saha) */}
            {finansGorunur && (
                <section className="mb-4 rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-2.5 flex items-center justify-between gap-3">
                        <h2 className="flex items-center gap-2 text-[14px] font-bold tracking-tight text-slate-900">
                            <i className="fas fa-chart-line text-vurgu-600" /> Finansal Özet
                        </h2>
                        <button
                            type="button"
                            onClick={handleToggleFinancialVisibility}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                        >
                            <i className={`fas ${isFinancialHidden ? 'fa-eye' : 'fa-eye-slash'}`} />
                            {isFinancialHidden ? 'Göster' : 'Gizle'}
                        </button>
                    </div>

                    {/* ── BUGÜN ── */}
                    {bolumBasligi('Bugün')}
                    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
                        {hucre('Satış', fin?.todaySales, `Kâr ${formatNumberHidden(satisKariGun)} ₺`,
                               satisKariGun < 0 ? 'text-rose-600' : 'text-emerald-600')}
                        {hucre('Tahsilat', tahsilatToplam,
                               veresiye.tutar > 0 ? `Veresiye ${formatNumberHidden(veresiye.tutar)} ₺` : 'Tamamı tahsil edildi',
                               veresiye.tutar > 0 ? 'text-amber-600' : 'text-slate-400')}
                        {hucre('Gider', fin?.todayExpenses,
                               giderKirilim.length > 0
                                   ? giderKirilim.slice(0, 2).map(g => `${g.kategori} ${formatNumberHidden(g.tutar)}`).join(' · ')
                                   : 'Gider yok',
                               'text-slate-400', 'text-rose-600')}
                        {hucre('Net', fin?.todayProfit, 'Kâr − gider', 'text-slate-400',
                               (fin?.todayProfit ?? 0) < 0 ? 'text-rose-600' : 'text-emerald-600')}
                        {/* Saha servisi bugünün sayısı — ayrı bir satır olarak
                            durmasına gerek yok, BUGÜN ızgarasının 5. hücresi. */}
                        {hucre('Saha Servisi', dashboardData.sahaBugun?.kar ?? 0,
                               `${dashboardData.sahaBugun?.ziyaret ?? 0} ziyaret`
                                 + ((dashboardData.sahaBugun?.ucretsiz ?? 0) > 0
                                     ? ` · ${dashboardData.sahaBugun?.ucretsiz} ücretsiz` : ''),
                               'text-slate-400',
                               (dashboardData.sahaBugun?.kar ?? 0) < 0 ? 'text-rose-600' : 'text-emerald-600',
                               () => navigate('/service/saha-performans'))}
                    </div>

                    {/* ── BU AY ── */}
                    {bolumBasligi('Bu Ay')}
                    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
                        {hucre('Satış', fin?.monthlySales, `Kâr ${formatNumberHidden(satisKariAy)} ₺`,
                               satisKariAy < 0 ? 'text-rose-600' : 'text-emerald-600')}
                        {hucre('Tahsilat', aylikTahsilat,
                               (fin?.monthlySales ?? 0) > 0
                                   ? `Satışın %${Math.round((aylikTahsilat / (fin?.monthlySales || 1)) * 100)}'i`
                                   : '—')}
                        {hucre('Gider', fin?.monthlyExpenses, 'Aylık toplam', 'text-slate-400', 'text-rose-600')}
                        {hucre('Net', fin?.monthlyProfit, 'Kâr − gider', 'text-slate-400',
                               (fin?.monthlyProfit ?? 0) < 0 ? 'text-rose-600' : 'text-emerald-600')}
                        {/* Kâr oranı: satışın yüzde kaçı kâr kaldı. Rakamın
                            kendisi büyüklüğü söylüyor, oran VERİMİ söylüyor —
                            aylar arası karşılaştırma ancak bununla yapılır. */}
                        {hucre('Kâr Oranı', undefined,
                               (fin?.monthlySales ?? 0) > 0
                                   ? `${formatNumberHidden(satisKariAy)} ₺ kâr`
                                   : 'Satış yok',
                               'text-slate-400',
                               satisKariAy < 0 ? 'text-rose-600' : 'text-emerald-600',
                               undefined,
                               (fin?.monthlySales ?? 0) > 0
                                   ? `%${Math.round((satisKariAy / (fin?.monthlySales || 1)) * 100)}`
                                   : '—')}
                    </div>

                    {/* ── DURUM ── */}
                    {bolumBasligi('Durum')}
                    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
                        {hucre('Kasa', fin?.totalCashBalance, 'Nakit mevcut')}
                        {/* Tahsil edilemeyen kısım. Toplam Alacak aynı satırın sonunda
                            (13.09.2026'da "Açık Veresiye"nin yerine geri geldi). */}
                        {hucre('Vadesi Geçmiş', gecikmisAlacak,
                               gecikmisAlacak > 0
                                   ? `${gecikmisAdet} kayıt`
                                   : 'Gecikmiş yok',
                               'text-slate-400',
                               gecikmisAlacak > 0 ? 'text-rose-600' : 'text-slate-900',
                               () => navigate('/finance/borc-hatirlatma'))}
                        {hucre('Fazla Ödeme', musteriAvansi,
                               avansAdet > 0 ? `${avansAdet} müşteri fazla ödedi` : 'Yok',
                               'text-slate-400', 'text-indigo-600',
                               avansAdet > 0 ? avansAcKapa : undefined)}
                        {hucre('Stok Değeri', stokDegeri, 'Alış fiyatıyla')}
                        {/* 13.09.2026 (kullanıcı isteği): "Açık Veresiye" yerine TOPLAM ALACAK.
                            Yalnız ARTI bakiyeler toplanır — fazla ödeyenlerin eksi bakiyesi
                            DÜŞÜLMEZ (o bizim borcumuz, "Fazla Ödeme" kutusunda ayrı duruyor). */}
                        {hucre('Toplam Alacak', fin?.totalReceivables,
                               `Vadesi gelmemiş ${formatNumberHidden(Math.max((fin?.totalReceivables ?? 0) - gecikmisAlacak, 0))} ₺`,
                               'text-slate-400', 'text-amber-600')}
                    </div>

                    {/* ── FAZLA ÖDEYENLER (kutuya tıklanınca) ──
                        İsme tıklayınca carinin hareketlerine gider: fazlanın nereden
                        doğduğu (mükerrer tahsilat mı, avans mı) orada görülür. */}
                    {avansAcik && (
                        <div className="mt-1.5 overflow-hidden rounded-lg border border-indigo-200">
                            <div className="flex items-center justify-between gap-2 bg-indigo-50 px-2 py-1.5 sm:px-2.5">
                                <span className="text-[11.5px] font-bold text-indigo-900">Fazla ödeyen müşteriler</span>
                                <button type="button" onClick={() => setAvansAcik(false)}
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-800 hover:text-indigo-950">
                                    Kapat <i className="fas fa-times text-[10px]" />
                                </button>
                            </div>
                            {avansHata ? (
                                <div className="bg-white px-2.5 py-2 text-[12px] text-rose-700">{avansHata}</div>
                            ) : avansListe === null ? (
                                <div className="bg-white px-2.5 py-2 text-[12px] text-slate-500">Yükleniyor…</div>
                            ) : avansListe.length === 0 ? (
                                <div className="bg-white px-2.5 py-2 text-[12px] text-slate-500">Fazla ödeyen müşteri kalmadı.</div>
                            ) : (
                                <div className="flex flex-col gap-px bg-slate-200">
                                    {avansListe.map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => navigate(`/transactions/${m.id}`)}
                                            className="flex w-full items-baseline justify-between gap-2 bg-white px-2 py-1.5 text-left transition-colors hover:bg-slate-50 sm:px-2.5"
                                        >
                                            <span className="flex min-w-0 items-baseline gap-1.5">
                                                <span className="truncate text-[12px] font-semibold text-slate-800">{m.ad || 'İsimsiz müşteri'}</span>
                                                {m.phone && <span className="shrink-0 text-[10.5px] tabular-nums text-slate-400">{m.phone}</span>}
                                            </span>
                                            <span className="flex shrink-0 items-baseline gap-2">
                                                <b className="text-[13px] font-bold tabular-nums text-indigo-700">
                                                    {formatNumberHidden(m.tutar)} ₺
                                                </b>
                                                <i className="fas fa-chevron-right text-[8px] text-slate-400" />
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── BUGÜNKÜ TAHSİLAT · ÖDEME TİPİ ──
                        Kaynak `todayPaymentMethods` DEĞİL: o satış tutarını ödeme
                        tipine göre toplar ve veresiye satışı da içerir — kasaya
                        girmemiş parayı tahsilat gibi gösterirdi. */}
                    {(tahsilatlar.length > 0 || veresiye.tutar > 0) && (
                        <>
                            {bolumBasligi('Bugünkü Tahsilat · Ödeme Tipi')}
                            <div className="flex flex-col gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
                                {tahsilatlar.map((k) => {
                                    const pay = tahsilatToplam > 0 ? (k.tutar / tahsilatToplam) * 100 : 0;
                                    return (
                                        /* Tıklanınca bugünün O ÖDEME TİPİNDEKİ işlemlerine gider —
                                           "2.238 ₺ nakit geldi, kimlerden?" sorusunun cevabı. */
                                        <button
                                            key={k.yontem}
                                            type="button"
                                            onClick={() => navigate(`/invoices/all-transactions?tarih=today&odeme=${encodeURIComponent(k.yontem)}`)}
                                            className="relative w-full overflow-hidden bg-white text-left transition-colors hover:bg-slate-50"
                                        >
                                            {/* Pay dolgusu metnin ARKASINDA — ayrı bir grafik
                                                kutusu açmadan oran okunabilsin diye. */}
                                            <div className="absolute inset-y-0 left-0 bg-emerald-500/12"
                                                 style={{ width: `${Math.max(pay, 2)}%` }} aria-hidden />
                                            <div className="relative flex items-baseline justify-between gap-2 px-2 py-1.5 sm:px-2.5">
                                                <span className="flex min-w-0 items-baseline gap-1.5">
                                                    <span className="truncate text-[12px] font-semibold text-slate-800">{k.yontem}</span>
                                                    <span className="shrink-0 text-[10.5px] tabular-nums text-slate-400">{k.adet} işlem</span>
                                                </span>
                                                <span className="flex shrink-0 items-baseline gap-2">
                                                    <span className="text-[10.5px] tabular-nums text-slate-400">%{pay.toFixed(0)}</span>
                                                    <b className="text-[13px] font-bold tabular-nums text-slate-900">
                                                        {formatNumberHidden(k.tutar)} ₺
                                                    </b>
                                                    <i className="fas fa-chevron-right text-[8px] text-slate-400" />
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}

                                {/* Veresiye tahsilat DEĞİLDİR: yüzde payına girmez.
                                    Sarı zemin "bu para henüz kasada değil" demek. */}
                                {veresiye.tutar > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => navigate('/invoices/all-transactions?tarih=today&tip=sales_invoice&odeme=Veresiye')}
                                        className="flex w-full items-baseline justify-between gap-2 bg-amber-50 px-2 py-1.5 text-left transition-colors hover:bg-amber-100 sm:px-2.5">
                                        <span className="flex min-w-0 items-baseline gap-1.5">
                                            <span className="truncate text-[12px] font-semibold text-amber-900">Veresiye</span>
                                            <span className="shrink-0 text-[10.5px] tabular-nums text-amber-700/70">
                                                {veresiye.adet} fatura · kasaya girmedi
                                            </span>
                                        </span>
                                        <span className="flex shrink-0 items-baseline gap-2">
                                            <b className="text-[13px] font-bold tabular-nums text-amber-700">
                                                {formatNumberHidden(veresiye.tutar)} ₺
                                            </b>
                                            <i className="fas fa-chevron-right text-[8px] text-amber-500" />
                                        </span>
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                </section>
            )}

            {/* 03.10.2026 (Arif): DURUM KARTLARI kaldırıldı. */}

            {/* ══════════════ SON SERVİSLER  |  BUGÜNKÜ SAHA İŞLERİ ══════════════
                Stitch tasarımı: sol geniş tablo, sağ dar saha kartı. Dar ekranda alt alta. */}
            <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                    <h2 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900">
                        Son Servis Kayıtları
                        <span className="pv2-rozet rounded-full px-2 py-0.5 text-[11px] font-semibold">
                            {formatNumber(dashboardData.openServices)} açık
                        </span>
                    </h2>
                    <button
                        type="button"
                        onClick={() => navigate('/service/pending-services')}
                        className="shrink-0 text-[12.5px] font-semibold text-vurgu-600 hover:underline"
                    >
                        Tüm kayıtlar →
                    </button>
                </div>

                {dashboardData.recentServices.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] table-fixed text-left">
                            <colgroup>
                                <col className="w-[92px]" /><col className="w-[24%]" /><col />
                                <col className="w-[124px]" /><col className="w-[84px]" /><col className="w-[88px]" />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    {['Fiş No', 'Müşteri', 'Cihaz / Arıza', 'Durum', 'Tutar', 'Tarih'].map((b, i) => (
                                        <th key={b} className={`whitespace-nowrap px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-500 ${i >= 4 ? 'text-right' : ''}`}>
                                            {b}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardData.recentServices.map((service: any) => {
                                    const durumMetni = String(service.durum || service.servisDurumu || service.asamaDurumu || '').toLowerCase();
                                    const teslimEdildi = ['teslim-edildi', 'teslim edildi', 'delivered'].includes(durumMetni);
                                    const musteriId = service.customer_id || service.customer?.id || service.musteriId;

                                    // Durum rozetinin rengi: bitmiş yeşil, parça bekleyen turuncu,
                                    // teslime hazır camgöbeği, geri kalanı nötr.
                                    // Renkler dashboard-content.css `.pv2-durum--*` (açık + koyu tema).
                                    const rozet = teslimEdildi
                                        ? 'pv2-durum--yesil'
                                        : durumMetni.includes('parça') || durumMetni.includes('parca')
                                        ? 'pv2-durum--turuncu'
                                        : durumMetni.includes('teslim')
                                        ? 'pv2-durum--mavi'
                                        : 'pv2-durum--notr';

                                    return (
                                        <tr
                                            key={service.id}
                                            onClick={() => navigate(teslimEdildi ? `/service/service-detail/${service.id}` : `/service/service-workflow/${service.id}`)}
                                            className="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50"
                                        >
                                            <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[12px] font-semibold text-vurgu-600">
                                                {service.servisKodu || '-'}
                                            </td>
                                            <td className="truncate px-3 py-2.5 text-[13px]">
                                                <span
                                                    onClick={(ev) => { if (musteriId) { ev.stopPropagation(); navigate(`/transactions/${musteriId}`); } }}
                                                    className={musteriId ? 'font-medium text-slate-900 hover:text-vurgu-600 hover:underline' : 'text-slate-800'}
                                                    title={musteriId ? 'Müşteri hareketlerini görüntüle' : ''}
                                                >
                                                    {service.musteriAdi || service.customer?.name || '-'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2" title={[service.urunAdi, service.arizaTanimi].filter(Boolean).join(' — ')}>
                                                <span className="block truncate text-[13px] text-slate-800">{service.urunAdi || '-'}</span>
                                                {service.arizaTanimi && (
                                                    <span className="block truncate text-[11.5px] text-slate-500">{service.arizaTanimi}</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <span className={`pv2-durum inline-flex max-w-full items-center truncate whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${rozet}`}>
                                                    {getStatusText(service.durum || service.servisDurumu || service.asamaDurumu)}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-right text-[12.5px] font-semibold tabular-nums text-slate-800">
                                                {service.sonTutar
                                                    ? `${service.paraBirimi === 'USD' ? '$' : '₺'}${parseFloat(service.sonTutar).toLocaleString('tr-TR')}`
                                                    : '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-right text-[12px] tabular-nums text-slate-500">
                                                {formatDate(service.olusturmaTarihi)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="px-5 py-10 text-center">
                        <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-[22px] text-slate-400">
                            <i className="fas fa-inbox" />
                        </span>
                        <p className="mt-4 text-[14px] font-medium text-slate-700">Henüz servis kaydı yok</p>
                        <p className="mt-1 text-[13px] text-slate-500">
                            Cihaz kabulü müşteri kaydı üzerinden başlar.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/customer/customer-list')}
                            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-vurgu-500 px-5 py-2.5 text-[14px] font-semibold text-white shadow-lg shadow-vurgu-600/25 transition-colors hover:bg-vurgu-600"
                        >
                            <i className="fas fa-plus" /> Müşteri seçip servis kaydı aç
                        </button>
                    </div>
                )}
            </section>

            {/* ── BUGÜNKÜ SAHA İŞLERİ ── */}
            <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                    <h2 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900">
                        <i className="fas fa-truck-field text-vurgu-600" /> Saha İşleri
                    </h2>
                    <span className="pv2-rozet rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        {sahaBugunSayisi > 0 ? `Bugün ${sahaBugunSayisi}` : `${(sahaIsleri || []).length} açık`}
                    </span>
                </div>
                <div className="flex-1">
                    {sahaIsleri === null ? (
                        <p className="px-4 py-6 text-[13px] text-slate-500">Yükleniyor…</p>
                    ) : sahaGosterilen.length === 0 ? (
                        <p className="px-4 py-6 text-[13px] text-slate-500">Açık saha işi yok.</p>
                    ) : sahaGosterilen.map((s) => {
                        const bugun = (s.randevuTarihi || '').slice(0, 10) === bugunIso;
                        const zaman = s.randevuSaati
                            ? s.randevuSaati.slice(0, 5)
                            : s.randevuTarihi ? formatDate(s.randevuTarihi) : 'Randevusuz';
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => navigate(`/service/saha-servis?is=${s.id}`)}
                                className="block w-full border-b border-slate-100 px-4 py-2.5 text-left transition-colors last:border-0 hover:bg-slate-50"
                            >
                                <span className="flex items-center justify-between gap-2">
                                    <span className={`pv2-saat rounded px-1.5 py-0.5 font-mono text-[11.5px] font-semibold ${bugun ? 'pv2-saat--bugun' : ''}`}>
                                        {bugun ? zaman : `${s.randevuTarihi ? formatDate(s.randevuTarihi) : ''} ${s.randevuSaati ? s.randevuSaati.slice(0, 5) : ''}`.trim() || zaman}
                                    </span>
                                    {s.teknisyen && <span className="truncate text-[11.5px] text-slate-500">{s.teknisyen}</span>}
                                </span>
                                <span className="mt-1 block truncate text-[13px] font-semibold text-slate-900">
                                    {s.musteri?.ad || 'Müşterisiz iş'}{s.cihaz ? ` — ${s.cihaz}` : ''}
                                </span>
                                {(s.adres || s.ariza) && (
                                    <span className="mt-0.5 block truncate text-[12px] text-slate-500">
                                        {s.adres ? <><i className="fas fa-location-dot mr-1 text-[10px]" />{s.adres}</> : s.ariza}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/service/saha-servis')}
                    className="m-3 rounded-lg border border-slate-200 bg-slate-50 py-2 text-[12.5px] font-semibold text-vurgu-600 transition-colors hover:bg-slate-100"
                >
                    <i className="fas fa-map-location-dot mr-1.5" /> Saha Servisine Git
                </button>
            </section>
            </div>

        </div>
    );
}

export default DashboardContent;
